import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getAdminToken, setAdminToken, inr, imgUrl } from "@/lib/api";
import {
  LayoutDashboard, Store, Package, Calendar, MessageSquare, UserPlus, LogOut, Trash2, Plus, X, Search, Eye, Pencil, Ban, Download, IndianRupee, Lock, ListChecks, Users, Inbox, Bell, FileText, Sparkles, Building2, ShieldCheck, KeyRound, CheckCircle2, Hourglass, Gift,
} from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { GalleryEditor } from "@/components/GalleryEditor";
import { ImageUpload } from "@/components/ImageUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "vendors", label: "Vendors", icon: Store },
  { id: "services", label: "Services", icon: Package },
  { id: "catalog", label: "Service List", icon: ListChecks },
  { id: "packages", label: "Packages", icon: Gift },
  { id: "svccats", label: "Service Cats", icon: Sparkles },
  { id: "halls", label: "Halls", icon: Building2 },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "customers", label: "Customers", icon: Users },
  { id: "finance", label: "Finance", icon: IndianRupee },
  { id: "enquiries", label: "Inquiries", icon: Inbox },
  { id: "approvals", label: "Approvals", icon: ShieldCheck },
  { id: "reminders", label: "Reminders", icon: Bell },
  { id: "applications", label: "Applications", icon: UserPlus },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!getAdminToken()) navigate("/admin/login");
  }, [navigate]);

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-16 lg:w-60 bg-foreground text-white flex flex-col shrink-0">
        <div className="h-16 flex items-center gap-2 px-4 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center font-display font-extrabold">B</div>
          <span className="hidden lg:block font-display font-bold">Admin</span>
        </div>
        <nav className="flex-1 py-4 grid gap-1 px-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-3 px-3 h-11 rounded-lg text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-primary text-white" : "text-white/60 hover:bg-white/10"
                }`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="hidden lg:block">{t.label}</span>
              </button>
            );
          })}
        </nav>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="flex items-center gap-3 px-5 h-14 text-sm font-semibold text-white/60 hover:bg-white/10"
        >
          <LogOut className="h-5 w-5" /> <span className="hidden lg:block">Logout</span>
        </button>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-auto">
        <header className="h-16 border-b border-stone-200 flex items-center px-6 bg-card">
          <h1 className="font-display font-bold text-xl capitalize">{tab}</h1>
        </header>
        <div className="p-6">
          {tab === "overview" && <Overview />}
          {tab === "vendors" && <Vendors />}
          {tab === "services" && <Services />}
          {tab === "catalog" && <Catalog />}
          {tab === "packages" && <PackagesAdmin />}
          {tab === "svccats" && <ServiceCategoriesAdmin />}
          {tab === "halls" && <HallsAdmin />}
          {tab === "bookings" && <Bookings />}
          {tab === "customers" && <Customers />}
          {tab === "finance" && <Finance />}
          {tab === "enquiries" && <InquiriesInbox />}
          {tab === "approvals" && <AdminApprovals />}
          {tab === "reminders" && <Reminders />}
          {tab === "applications" && <Applications />}
        </div>
      </main>
    </div>
  );
}

function downloadCSV(name, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
const toArr = (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v || []);
const monthKey = (iso) => (iso || "").slice(0, 7);
const lastMonths = (n) => {
  const base = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base.getFullYear(), base.getMonth() - (n - 1 - i), 1);
    return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }) };
  });
};
const fmtDay = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function Overview() {
  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
  });
  if (!stats) return <p className="text-muted-foreground">Loading...</p>;
  const cards = [
    { label: "Revenue (paid)", value: inr(stats.revenue) },
    { label: "Vendors", value: stats.vendors },
    { label: "Services", value: stats.services },
    { label: "Total bookings", value: stats.bookings },
    { label: "Paid bookings", value: stats.paid_bookings },
    { label: "Enquiries", value: stats.enquiries },
    { label: "Pending applications", value: stats.pending_applications },
  ];
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-stone-200 rounded-2xl p-5">
            <p className="text-sm text-muted-foreground">{c.label}</p>
            <p className="font-display font-extrabold text-2xl mt-1">{c.value}</p>
          </div>
        ))}
      </div>
      <RevenueChart />
    </div>
  );
}

function RevenueChart() {
  const { data: bookings = [] } = useQuery({ queryKey: ["admin-bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });
  const { data: expenses = [] } = useQuery({ queryKey: ["admin-expenses"], queryFn: async () => (await api.get("/admin/expenses")).data });
  const total = (r) => r.final_total ?? r.subtotal ?? 0;
  const months = lastMonths(6);
  const data = months.map((m) => {
    const bk = bookings.filter((b) => monthKey(b.created_at) === m.key && b.status !== "cancelled");
    const income = bk.reduce((s, b) => s + (b.amount_paid || 0), 0);
    const payouts = bk.reduce((s, b) => s + (b.vendor_payout_paid || 0), 0);
    const exp = expenses.filter((e) => e.status === "paid" && monthKey(e.expense_date || e.created_at) === m.key).reduce((s, e) => s + (e.amount || 0), 0);
    return { month: m.label, Income: income, Payouts: payouts, Expenses: exp, Profit: income - payouts - exp };
  });
  return (
    <div className="bg-card border border-stone-200 rounded-2xl p-5 mt-6" data-testid="revenue-analytics">
      <h3 className="font-display font-bold mb-1">Revenue analytics</h3>
      <p className="text-xs text-muted-foreground mb-4">Last 6 months — income collected, vendor payouts, expenses & profit.</p>
      <div style={{ width: "100%", height: 320, minHeight: 320 }}>
        <ResponsiveContainer minHeight={320}>
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="month" fontSize={12} />
            <YAxis fontSize={12} width={70} tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)} />
            <Tooltip formatter={(v) => inr(v)} />
            <Legend />
            <Bar dataKey="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Payouts" fill="#f43f5e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Expenses" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="Profit" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Table({ columns, rows, testid }) {
  if (!rows?.length)
    return <p className="text-muted-foreground" data-testid={`${testid}-empty`}>No records yet.</p>;
  return (
    <div className="overflow-x-auto border border-stone-200 rounded-xl bg-card" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200 text-left text-muted-foreground">
            {columns.map((c) => (
              <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-stone-100 last:border-0">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 align-top">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Vendors() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = {
    name: "", city: "Kundapura", event_types: [], cover: "", rating: 4.5, reviews: 0, price_range: "₹₹",
    featured: false, tags: [], description: "", active: true,
    business_type: "", address: "", contact_email: "", contact_phone: "",
    bank_name: "", bank_account_name: "", bank_account_number: "", bank_ifsc: "", upi_id: "", notes: "",
    gallery: [],
  };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const { data: vendors = [] } = useQuery({
    queryKey: ["admin-vendors"],
    queryFn: async () => (await api.get("/admin/vendors")).data,
  });

  const openAdd = () => { setEditId(null); setForm(empty); setShowForm(true); };
  const openEdit = (v) => {
    setEditId(v.id);
    setForm({ ...empty, ...v, event_types: (v.event_types || []).join(", "), tags: (v.tags || []).join(", ") });
    setShowForm(true);
  };
  const close = () => { setShowForm(false); setEditId(null); setForm(empty); };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const payload = { ...form, event_types: toArr(form.event_types), tags: toArr(form.tags), rating: Number(form.rating) || 4.5, reviews: Number(form.reviews) || 0 };
    try {
      if (editId) await api.put(`/admin/vendors/${editId}`, payload);
      else await api.post("/admin/vendors", payload);
      toast.success(editId ? "Vendor updated" : "Vendor added");
      close();
      qc.invalidateQueries({ queryKey: ["admin-vendors"] });
    } catch { toast.error("Failed to save vendor"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this vendor and their services?")) return;
    await api.delete(`/admin/vendors/${id}`);
    toast.success("Vendor deleted");
    qc.invalidateQueries({ queryKey: ["admin-vendors"] });
  };

  return (
    <div>
      <button
        data-testid="admin-add-vendor-btn"
        onClick={() => (showForm ? close() : openAdd())}
        className="mb-4 flex items-center gap-2 h-10 px-4 bg-primary text-white rounded-full text-sm font-semibold"
      >
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        {showForm ? "Close" : "Add vendor"}
      </button>

      {showForm && (
        <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-6 grid md:grid-cols-2 gap-3" data-testid="admin-vendor-form">
          <p className="md:col-span-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Basics</p>
          <Input placeholder="Vendor / business name" data-testid="av-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
          <Input placeholder="Type of business (e.g. Decorator, DJ, Caterer)" data-testid="av-type" value={form.business_type} onChange={(e) => set("business_type", e.target.value)} />
          <Input placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} />
          <Input placeholder="Price range e.g. ₹₹" value={form.price_range} onChange={(e) => set("price_range", e.target.value)} />
          <Input placeholder="Event types (comma separated slugs)" value={form.event_types} onChange={(e) => set("event_types", e.target.value)} />
          <Input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
          <Input placeholder="Cover image URL" value={form.cover} onChange={(e) => set("cover", e.target.value)} />
          <label className="flex items-center gap-2 text-sm px-1"><input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on Home</label>

          <p className="md:col-span-2 text-xs font-bold uppercase tracking-wide text-muted-foreground mt-2">Contact & address</p>
          <Input placeholder="Office email ID" data-testid="av-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
          <Input placeholder="Contact phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} />
          <Textarea placeholder="Office address" className="md:col-span-2" value={form.address} onChange={(e) => set("address", e.target.value)} />

          <p className="md:col-span-2 text-xs font-bold uppercase tracking-wide text-muted-foreground mt-2">Banking info (for payouts)</p>
          <Input placeholder="Bank name" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} />
          <Input placeholder="Account holder name" value={form.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)} />
          <Input placeholder="Account number" data-testid="av-acct" value={form.bank_account_number} onChange={(e) => set("bank_account_number", e.target.value)} />
          <Input placeholder="IFSC code" value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value)} />
          <Input placeholder="UPI ID" value={form.upi_id} onChange={(e) => set("upi_id", e.target.value)} />

          <p className="md:col-span-2 text-xs font-bold uppercase tracking-wide text-muted-foreground mt-2">Other info</p>
          <Textarea placeholder="Description (shown to customers)" className="md:col-span-2" value={form.description} onChange={(e) => set("description", e.target.value)} />
          <Textarea placeholder="Custom vendor notes (internal)" className="md:col-span-2" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          <div className="md:col-span-2">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Past events gallery</p>
            <GalleryEditor value={form.gallery} onChange={(g) => set("gallery", g)} testid="av-gallery" />
          </div>
          <p className="md:col-span-2 text-xs text-muted-foreground">Tip: add this vendor's services & prices from the <b>Services</b> tab.</p>
          {editId && (
            <div className="md:col-span-2 border-t border-stone-200 pt-3">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">Services offered & prices</p>
              <VendorServices vendorId={editId} />
            </div>
          )}
          {editId && (
            <div className="md:col-span-2 border-t border-stone-200 pt-3">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Vendor app login</p>
              <VendorCredentials vendorId={editId} currentEmail={form.email} canLogin={!!form.can_login} onDone={() => qc.invalidateQueries({ queryKey: ["admin-vendors"] })} />
            </div>
          )}
          <button data-testid="av-save" onClick={save} className="md:col-span-2 h-10 bg-primary text-white rounded-full font-semibold">{editId ? "Save changes" : "Save vendor"}</button>
        </div>
      )}

      <Table
        testid="admin-vendors-table"
        columns={[
          { key: "name", label: "Name", render: (r) => (
            <div><p className="font-semibold">{r.name}</p>{r.business_type ? <p className="text-xs text-muted-foreground">{r.business_type}</p> : null}</div>
          )},
          { key: "city", label: "City" },
          { key: "contact_email", label: "Contact", render: (r) => (
            <div className="text-xs">{r.contact_email || "—"}<br />{r.contact_phone || ""}</div>
          )},
          { key: "tags", label: "Tags", render: (r) => (r.tags || []).join(", ") },
          { key: "featured", label: "Featured", render: (r) => (r.featured ? "Yes" : "No") },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`edit-vendor-${r.id}`} onClick={() => openEdit(r)} className="text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`del-vendor-${r.id}`} onClick={() => del(r.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={vendors}
      />
    </div>
  );
}

function Services() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const empty = { vendor_id: "", name: "", type: "Stage Decoration", price: 0, unit: "per event", image: "", desc: "" };
  const [form, setForm] = useState(empty);

  const { data: vendors = [] } = useQuery({
    queryKey: ["admin-vendors"],
    queryFn: async () => (await api.get("/admin/vendors")).data,
  });
  const { data: services = [] } = useQuery({
    queryKey: ["admin-services"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const save = async () => {
    if (!form.vendor_id || !form.name) return toast.error("Vendor & name required");
    try {
      await api.post("/admin/services", { ...form, price: Number(form.price) });
      toast.success("Service added");
      setShowForm(false); setForm(empty);
      qc.invalidateQueries({ queryKey: ["admin-services"] });
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    await api.delete(`/admin/services/${id}`);
    toast.success("Deleted");
    qc.invalidateQueries({ queryKey: ["admin-services"] });
  };

  return (
    <div>
      <button data-testid="admin-add-service-btn" onClick={() => setShowForm((s) => !s)} className="mb-4 flex items-center gap-2 h-10 px-4 bg-primary text-white rounded-full text-sm font-semibold">
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {showForm ? "Close" : "Add service"}
      </button>
      {showForm && (
        <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-6 grid md:grid-cols-2 gap-3" data-testid="admin-service-form">
          <select data-testid="as-vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="h-10 rounded-md border border-input bg-card px-3 text-sm">
            <option value="">Select vendor</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <Input placeholder="Service name" data-testid="as-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
          <Input placeholder="Price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <Input placeholder="Unit e.g. per event" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          <Input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          <Textarea placeholder="Description" className="md:col-span-2" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} />
          <button data-testid="as-save" onClick={save} className="md:col-span-2 h-10 bg-primary text-white rounded-full font-semibold">Save service</button>
        </div>
      )}
      <Table
        testid="admin-services-table"
        columns={[
          { key: "name", label: "Service" },
          { key: "vendor", label: "Vendor", render: (r) => vendorName(r.vendor_id) },
          { key: "type", label: "Type" },
          { key: "price", label: "Price", render: (r) => inr(r.price) },
          { key: "unit", label: "Unit" },
          { key: "actions", label: "", render: (r) => (
            <button data-testid={`del-service-${r.id}`} onClick={() => del(r.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
          )},
        ]}
        rows={services}
      />
    </div>
  );
}

function ServiceCategoriesAdmin() {
  const qc = useQueryClient();
  const empty = { name: "", description: "", image: "", active: true };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { data: cats = [] } = useQuery({ queryKey: ["admin-svccats"], queryFn: async () => (await api.get("/admin/service-categories")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-svccats"] });
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) await api.put(`/admin/service-categories/${editId}`, form);
      else await api.post("/admin/service-categories", form);
      toast.success(editId ? "Category updated" : "Category added");
      setForm(empty); setEditId(null); refresh();
    } catch { toast.error("Failed to save category"); }
  };
  const edit = (r) => { setEditId(r.id); setForm({ ...empty, ...r }); };
  const del = async (id) => { if (!window.confirm("Delete this service category?")) return; await api.delete(`/admin/service-categories/${id}`); toast.success("Deleted"); refresh(); };
  return (
    <div data-testid="admin-svccats">
      <p className="text-sm text-muted-foreground mb-4">Service categories power the <b>By Service</b> browse page (e.g. Stage Decoration, DJ, Catering, Shamiyana).</p>
      <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-4 grid md:grid-cols-2 gap-4 items-start">
        <div className="grid gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Category name</label>
            <Input data-testid="svccat-name" placeholder="e.g. Stage Decoration" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Description</label>
            <Textarea data-testid="svccat-desc" placeholder="Short description" value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="svccat-active" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active (visible to customers)
          </label>
          <div className="flex gap-2">
            <button data-testid="svccat-save" onClick={save} className="h-10 px-6 bg-primary text-white rounded-full font-semibold">{editId ? "Save category" : "Add category"}</button>
            {editId && <button data-testid="svccat-cancel" onClick={() => { setEditId(null); setForm(empty); }} className="h-10 px-6 border border-stone-300 rounded-full font-semibold">Cancel</button>}
          </div>
        </div>
        <div>
          <ImageUpload value={form.image} onChange={(url) => set("image", url)} label="Category image" testid="svccat-image" />
        </div>
      </div>
      <Table
        testid="admin-svccats-table"
        columns={[
          { key: "image", label: "", render: (r) => r.image ? <img src={imgUrl(r.image)} alt={r.name} className="h-10 w-14 object-cover rounded" /> : <div className="h-10 w-14 bg-muted rounded" /> },
          { key: "name", label: "Category" },
          { key: "description", label: "Description", render: (r) => r.description || "—" },
          { key: "active", label: "Status", render: (r) => r.active ? <span className="text-emerald-700 font-semibold">Active</span> : <span className="text-muted-foreground">Hidden</span> },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`svccat-edit-${r.id}`} onClick={() => edit(r)} className="text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`svccat-del-${r.id}`} onClick={() => del(r.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={cats}
      />
    </div>
  );
}

function HallsAdmin() {
  const qc = useQueryClient();
  const empty = {
    name: "", hall_type: "Kalyana Mantapa", city: "Kundapura", address: "",
    seat_capacity: 0, dining_capacity: 0, ac: "Non-AC", parking_capacity: 0,
    outside_catering_allowed: true, outside_decoration_allowed: true,
    price: 0, cover: "", gallery: [], amenities: [], description: "", active: true,
  };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { data: halls = [] } = useQuery({ queryKey: ["admin-halls"], queryFn: async () => (await api.get("/admin/halls")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-halls"] });
  const openNew = () => { setEditId(null); setForm(empty); setShowForm(true); };
  const edit = (r) => { setEditId(r.id); setForm({ ...empty, ...r, amenities: r.amenities || [], gallery: r.gallery || [] }); setShowForm(true); };
  const save = async () => {
    if (!form.name) return toast.error("Hall name required");
    const payload = {
      ...form,
      seat_capacity: Number(form.seat_capacity) || 0,
      dining_capacity: Number(form.dining_capacity) || 0,
      parking_capacity: Number(form.parking_capacity) || 0,
      price: Number(form.price) || 0,
      amenities: Array.isArray(form.amenities) ? form.amenities : String(form.amenities || "").split(",").map((a) => a.trim()).filter(Boolean),
    };
    try {
      if (editId) await api.put(`/admin/halls/${editId}`, payload);
      else await api.post("/admin/halls", payload);
      toast.success(editId ? "Hall updated" : "Hall added");
      setForm(empty); setEditId(null); setShowForm(false); refresh();
    } catch { toast.error("Failed to save hall"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this hall?")) return; await api.delete(`/admin/halls/${id}`); toast.success("Deleted"); refresh(); };

  const amenitiesStr = Array.isArray(form.amenities) ? form.amenities.join(", ") : (form.amenities || "");

  return (
    <div data-testid="admin-halls">
      <p className="text-sm text-muted-foreground mb-4">Manage halls, choultries & convention centres shown on the <b>Halls/Choultry</b> page.</p>
      <button data-testid="admin-add-hall-btn" onClick={showForm ? () => setShowForm(false) : openNew} className="mb-4 flex items-center gap-2 h-10 px-4 bg-primary text-white rounded-full text-sm font-semibold">
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {showForm ? "Close" : "Add hall"}
      </button>
      {showForm && (
        <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-6 grid md:grid-cols-2 gap-4" data-testid="admin-hall-form">
          <div className="grid gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Hall name</label>
              <Input data-testid="hall-name" placeholder="e.g. Sri Krishna Kalyana Mantapa" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Type</label>
                <select data-testid="hall-type" value={form.hall_type} onChange={(e) => set("hall_type", e.target.value)} className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm">
                  {["Kalyana Mantapa", "Choultry", "Convention Centre", "Banquet Hall", "Hall"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">AC / Non-AC</label>
                <select data-testid="hall-ac" value={form.ac} onChange={(e) => set("ac", e.target.value)} className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm">
                  {["Non-AC", "AC", "AC & Non-AC"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">City</label>
                <Input data-testid="hall-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Price / event (₹)</label>
                <Input data-testid="hall-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Address</label>
              <Input data-testid="hall-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Seating</label>
                <Input data-testid="hall-seat" type="number" value={form.seat_capacity} onChange={(e) => set("seat_capacity", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Dining</label>
                <Input data-testid="hall-dining" type="number" value={form.dining_capacity} onChange={(e) => set("dining_capacity", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Parking</label>
                <Input data-testid="hall-parking" type="number" value={form.parking_capacity} onChange={(e) => set("parking_capacity", e.target.value)} />
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="hall-catering" checked={form.outside_catering_allowed} onChange={(e) => set("outside_catering_allowed", e.target.checked)} /> Outside catering allowed
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="hall-decor" checked={form.outside_decoration_allowed} onChange={(e) => set("outside_decoration_allowed", e.target.checked)} /> Outside decoration allowed
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="hall-active" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active
              </label>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Amenities (comma separated)</label>
              <Input data-testid="hall-amenities" placeholder="Green room, Generator, Sound system" value={amenitiesStr} onChange={(e) => set("amenities", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Description</label>
              <Textarea data-testid="hall-desc" value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-4 content-start">
            <ImageUpload value={form.cover} onChange={(url) => set("cover", url)} label="Cover photo" testid="hall-cover" />
            <div>
              <p className="text-sm font-semibold mb-1.5">Gallery</p>
              <GalleryEditor value={form.gallery} onChange={(g) => set("gallery", g)} testid="hall-gallery-editor" />
            </div>
            <button data-testid="hall-save" onClick={save} className="h-10 bg-primary text-white rounded-full font-semibold">{editId ? "Save hall" : "Add hall"}</button>
          </div>
        </div>
      )}
      <Table
        testid="admin-halls-table"
        columns={[
          { key: "cover", label: "", render: (r) => r.cover ? <img src={imgUrl(r.cover)} alt={r.name} className="h-10 w-14 object-cover rounded" /> : <div className="h-10 w-14 bg-muted rounded" /> },
          { key: "name", label: "Hall" },
          { key: "hall_type", label: "Type" },
          { key: "city", label: "City" },
          { key: "seat_capacity", label: "Seats", render: (r) => r.seat_capacity || "—" },
          { key: "ac", label: "AC" },
          { key: "price", label: "Price", render: (r) => inr(r.price) },
          { key: "active", label: "Status", render: (r) => r.active ? <span className="text-emerald-700 font-semibold">Active</span> : <span className="text-muted-foreground">Hidden</span> },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`hall-edit-${r.id}`} onClick={() => edit(r)} className="text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`hall-del-${r.id}`} onClick={() => del(r.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={halls}
      />
    </div>
  );
}

function VendorCredentials({ vendorId, currentEmail, canLogin, onDone }) {
  const [email, setEmail] = useState(currentEmail || "");
  const [password, setPassword] = useState("");
  const [resetPw, setResetPw] = useState("");
  const saveCreds = async () => {
    if (!email || !password) return toast.error("Email and password required");
    try {
      await api.put(`/admin/vendors/${vendorId}/credentials`, { email, password });
      toast.success("Login created/updated"); setPassword(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reset = async () => {
    if (!resetPw) return toast.error("Enter a new password");
    try {
      await api.put(`/admin/vendors/${vendorId}/reset-password`, { password: resetPw });
      toast.success("Password reset"); setResetPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid gap-3" data-testid="vendor-credentials">
      <p className="text-xs text-muted-foreground">
        {canLogin ? <span className="text-emerald-700 font-semibold">✓ Login active</span> : "No login yet"} — the vendor uses this email & password to sign in to the vendor app.
      </p>
      <div className="grid md:grid-cols-3 gap-2 items-end">
        <div>
          <label className="text-xs text-muted-foreground">Login email</label>
          <Input data-testid="vc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vendor@email.com" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{canLogin ? "New password" : "Set password"}</label>
          <Input data-testid="vc-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
        </div>
        <button data-testid="vc-save" onClick={saveCreds} className="h-10 bg-primary text-white rounded-full font-semibold text-sm">{canLogin ? "Update login" : "Create login"}</button>
      </div>
      {canLogin && (
        <div className="grid md:grid-cols-3 gap-2 items-end">
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">Reset password to</label>
            <Input data-testid="vc-reset" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password" />
          </div>
          <button data-testid="vc-reset-btn" onClick={reset} className="h-10 border border-stone-300 rounded-full font-semibold text-sm">Reset password</button>
        </div>
      )}
    </div>
  );
}

function AdminApprovals() {
  const [sub, setSub] = useState("media");
  return (
    <div data-testid="admin-approvals">
      <div className="flex gap-2 mb-5">
        <button data-testid="approvals-sub-media" onClick={() => setSub("media")} className={`h-9 px-4 rounded-full text-sm font-semibold ${sub === "media" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>Photo uploads</button>
        <button data-testid="approvals-sub-completions" onClick={() => setSub("completions")} className={`h-9 px-4 rounded-full text-sm font-semibold ${sub === "completions" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>Event completions</button>
      </div>
      {sub === "media" ? <MediaQueue /> : <CompletionsQueue />}
    </div>
  );
}

function MediaQueue() {
  const qc = useQueryClient();
  const { data: media = [] } = useQuery({ queryKey: ["admin-media"], queryFn: async () => (await api.get("/admin/vendor-media?status=pending")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-media"] });
  const moderate = async (id, action) => {
    try { await api.put(`/admin/vendor-media/${id}`, { action }); toast.success(action === "approve" ? "Approved — now live on profile" : "Rejected"); refresh(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="admin-media-queue">
      <p className="text-sm text-muted-foreground mb-4">Vendor-uploaded photos awaiting your approval. Approved photos appear automatically on the vendor's public profile.</p>
      {media.length === 0 ? <p className="text-muted-foreground text-sm" data-testid="media-queue-empty">Nothing pending. 🎉</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {media.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="bg-card border border-stone-200 rounded-2xl overflow-hidden">
              <img src={imgUrl(m.url)} alt={m.caption || "media"} className="w-full aspect-video object-cover" />
              <div className="p-3">
                <p className="font-semibold text-sm">{m.vendor_name || "Vendor"}</p>
                {m.caption && <p className="text-xs text-muted-foreground">{m.caption}</p>}
                <div className="flex gap-2 mt-3">
                  <button data-testid={`media-approve-${m.id}`} onClick={() => moderate(m.id, "approve")} className="flex-1 h-9 bg-emerald-600 text-white rounded-full text-sm font-semibold flex items-center justify-center gap-1"><CheckCircle2 className="h-4 w-4" /> Approve</button>
                  <button data-testid={`media-reject-${m.id}`} onClick={() => moderate(m.id, "reject")} className="flex-1 h-9 border border-stone-300 rounded-full text-sm font-semibold">Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompletionsQueue() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["admin-completions"], queryFn: async () => (await api.get("/admin/completions?status=pending_review")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-completions"] });
  const moderate = async (booking_id, vendor_id, action) => {
    try { await api.put(`/admin/completions`, { booking_id, vendor_id, action }); toast.success(action === "approve" ? "Approved — ready for settlement" : "Rejected"); refresh(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="admin-completions-queue">
      <p className="text-sm text-muted-foreground mb-4">Vendors submit a photo when an event is done. Approve to mark the booking ready for settlement.</p>
      {rows.length === 0 ? <p className="text-muted-foreground text-sm" data-testid="completions-queue-empty">Nothing pending. 🎉</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => (
            <div key={`${r.booking_id}-${r.vendor_id}`} data-testid={`completion-item-${r.booking_id}`} className="bg-card border border-stone-200 rounded-2xl overflow-hidden">
              {r.photo ? <img src={imgUrl(r.photo)} alt="completion" className="w-full aspect-video object-cover" /> : <div className="w-full aspect-video bg-muted grid place-items-center text-muted-foreground text-sm"><Hourglass className="h-5 w-5" /></div>}
              <div className="p-3">
                <p className="font-semibold text-sm">{r.vendor_name || "Vendor"}</p>
                <p className="text-xs text-muted-foreground capitalize">{r.event_type} · {r.customer_name}</p>
                {r.note && <p className="text-xs text-muted-foreground mt-1">"{r.note}"</p>}
                <div className="flex gap-2 mt-3">
                  <button data-testid={`completion-approve-${r.booking_id}`} onClick={() => moderate(r.booking_id, r.vendor_id, "approve")} className="flex-1 h-9 bg-emerald-600 text-white rounded-full text-sm font-semibold flex items-center justify-center gap-1"><CheckCircle2 className="h-4 w-4" /> Approve</button>
                  <button data-testid={`completion-reject-${r.booking_id}`} onClick={() => moderate(r.booking_id, r.vendor_id, "reject")} className="flex-1 h-9 border border-stone-300 rounded-full text-sm font-semibold">Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PackagesAdmin() {
  const qc = useQueryClient();
  const empty = { name: "", image: "", tagline: "", event_type: "", guest_label: "", discount_pct: 0, base_guests: 0, items: [], active: true, featured: false };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const { data: packages = [] } = useQuery({ queryKey: ["admin-packages"], queryFn: async () => (await api.get("/admin/packages")).data });
  const { data: services = [] } = useQuery({ queryKey: ["admin-all-services"], queryFn: async () => (await api.get("/services")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["admin-vendors"], queryFn: async () => (await api.get("/admin/vendors")).data });
  const { data: occasions = [] } = useQuery({ queryKey: ["admin-occasions"], queryFn: async () => (await api.get("/categories")).data });
  const vname = (id) => vendors.find((v) => v.id === id)?.name || "";
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-packages"] });

  const openAdd = () => { setEditId(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ ...empty, ...p, discount_pct: p.discount_pct || 0, items: (p.items || []).map((it) => ({ ...it })) }); setShowForm(true); };
  const close = () => { setShowForm(false); setEditId(null); setForm(empty); };

  const blankItem = { service_id: "", name: "", price: 0, quantity: 1, per_guest: false, unit: "per event", vendor_id: "", vendor_name: "" };
  const addRow = () => set("items", [...form.items, { ...blankItem }]);
  const updRow = (i, patch) => set("items", form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const updItem = (i, k, v) => updRow(i, { [k]: v });
  const rmItem = (i) => set("items", form.items.filter((_, idx) => idx !== i));
  const pickForRow = (i, serviceId) => {
    if (!serviceId) { updRow(i, { service_id: "", vendor_id: "", vendor_name: "" }); return; }
    const s = services.find((x) => x.id === serviceId);
    if (!s) return;
    updRow(i, { service_id: s.id, name: s.name, price: Number(s.price) || 0, unit: s.unit || "per event", vendor_id: s.vendor_id || "", vendor_name: vname(s.vendor_id) });
  };

  const bg = Number(form.base_guests) || 0;
  const subtotal = form.items.reduce((s, it) => s + (Number(it.price) || 0) * ((Number(it.quantity) || 1) * (it.per_guest && bg > 0 ? bg : 1)), 0);
  const discount = Number(form.discount_pct) || 0;
  const finalPrice = Math.round(subtotal * (1 - discount / 100));
  const etTrim = (form.event_type || "").trim().toLowerCase();
  const matchedOcc = etTrim ? occasions.find((o) => {
    const s = (o.slug || "").toLowerCase(), n = (o.name || "").toLowerCase();
    return etTrim === s || etTrim === n || (s && (s.includes(etTrim) || etTrim.includes(s))) || (n && (n.includes(etTrim) || etTrim.includes(n)));
  }) : null;

  const save = async () => {
    if (!form.name) return toast.error("Package name required");
    if (form.items.length === 0) return toast.error("Add at least one service");
    if (form.items.some((it) => !it.name)) return toast.error("Every item needs a name");
    const payload = { ...form, discount_pct: discount, base_guests: Number(form.base_guests) || 0, min_guests: Number(form.min_guests) || 0, max_guests: Number(form.max_guests) || 0, items: form.items.map((it) => ({ service_id: it.service_id || "", name: it.name, price: Number(it.price) || 0, quantity: Number(it.quantity) || 1, per_guest: !!it.per_guest, unit: it.unit || "per event", vendor_id: it.vendor_id || "", vendor_name: it.vendor_name || "" })) };
    try {
      if (editId) await api.put(`/admin/packages/${editId}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success(editId ? "Package updated" : "Package created");
      close(); refresh();
    } catch { toast.error("Failed to save package"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this package?")) return; await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); refresh(); };

  return (
    <div data-testid="admin-packages">
      <p className="text-sm text-muted-foreground mb-4">Build your own bundles: pick services from your list, set a special package price for each (originals stay unchanged), add a photo and an optional discount.</p>
      <button data-testid="admin-add-package-btn" onClick={showForm ? close : openAdd} className="mb-5 flex items-center gap-2 h-10 px-4 bg-primary text-white rounded-full text-sm font-semibold">
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {showForm ? "Close" : "Create package"}
      </button>

      {showForm && (
        <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-6 grid lg:grid-cols-2 gap-5" data-testid="admin-package-form">
          <div className="grid gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Package name</label>
              <Input data-testid="pkg-name" placeholder="e.g. Grand Wedding Combo" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Tagline</label>
              <Input data-testid="pkg-tagline" placeholder="Short catchy line" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Occasion</label>
                <Input data-testid="pkg-event" placeholder="e.g. Reception, Marriage" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Guest label</label>
                <Input data-testid="pkg-guests" placeholder="e.g. Up to 250" value={form.guest_label} onChange={(e) => set("guest_label", e.target.value)} />
              </div>
            </div>
            {etTrim && (
              matchedOcc ? (
                <p data-testid="pkg-occasion-hint" className="text-[11px] text-emerald-700 -mt-1">✓ Will appear on the <b>{matchedOcc.name}</b> occasion page.</p>
              ) : (
                <p data-testid="pkg-occasion-hint" className="text-[11px] text-amber-600 -mt-1">⚠ Won't match any occasion page. Try: {occasions.map((o) => o.name).join(", ") || "—"}</p>
              )
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Discount %</label>
                <Input data-testid="pkg-discount" type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Base guest count</label>
                <Input data-testid="pkg-base-guests" type="number" min="0" placeholder="e.g. 200" value={form.base_guests} onChange={(e) => set("base_guests", e.target.value)} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Tick “Scales per guest” on any item (e.g. chairs, plates) — its quantity multiplies by the guest count, and customers can adjust it on the package.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Min guests (optional)</label>
                <Input data-testid="pkg-min-guests" type="number" min="0" placeholder="e.g. 50" value={form.min_guests} onChange={(e) => set("min_guests", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Max guests (optional)</label>
                <Input data-testid="pkg-max-guests" type="number" min="0" placeholder="e.g. 1000" value={form.max_guests} onChange={(e) => set("max_guests", e.target.value)} />
              </div>
            </div>
            <ImageUpload value={form.image} onChange={(url) => set("image", url)} label="Package photo" testid="pkg-image" aspect="aspect-[3/1]" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pkg-active" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active (visible to customers)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="pkg-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> ⭐ Feature on home page
            </label>
          </div>

          <div className="grid gap-3 content-start">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Services in this package</p>
              <button data-testid="pkg-add-item" onClick={addRow} className="flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-full text-sm font-semibold"><Plus className="h-4 w-4" /> Add item</button>
            </div>
            <p className="text-xs text-muted-foreground">For each item, either pick a service from your list or choose “Custom item” and type it in. Editing the price here only affects this package.</p>

            <div className="grid gap-3">
              {form.items.length === 0 && <p className="text-xs text-muted-foreground">No items yet — tap “Add item” to start.</p>}
              {form.items.map((it, i) => (
                <div key={i} data-testid={`pkg-item-${i}`} className="bg-muted/40 rounded-xl p-3 grid gap-2">
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`pkg-item-pick-${i}`}
                      value={it.service_id || ""}
                      onChange={(e) => pickForRow(i, e.target.value)}
                      className="h-9 flex-1 rounded-md border border-input bg-card px-2 text-sm min-w-0"
                    >
                      <option value="">✏️ Custom item (type manually)</option>
                      {services.map((s) => <option key={s.id} value={s.id}>{s.name} — {vname(s.vendor_id)} ({inr(s.price)})</option>)}
                    </select>
                    <button data-testid={`pkg-item-del-${i}`} onClick={() => rmItem(i)} className="text-destructive h-9 w-9 grid place-items-center shrink-0"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    {it.service_id ? (
                      <p className="flex-1 min-w-0 text-sm font-semibold truncate px-1">{it.name}{it.vendor_name ? <span className="text-xs font-normal text-muted-foreground"> · {it.vendor_name}</span> : null}</p>
                    ) : (
                      <Input data-testid={`pkg-item-name-${i}`} placeholder="Item name (e.g. Chairs)" value={it.name} onChange={(e) => updItem(i, "name", e.target.value)} className="h-9 flex-1" />
                    )}
                    <div className="w-16 shrink-0">
                      <Input data-testid={`pkg-item-qty-${i}`} type="number" min="1" value={it.quantity ?? 1} onChange={(e) => updItem(i, "quantity", e.target.value)} className="h-9 text-center" title="Quantity" />
                    </div>
                    <div className="w-24 shrink-0">
                      <Input data-testid={`pkg-item-price-${i}`} type="number" value={it.price} onChange={(e) => updItem(i, "price", e.target.value)} className="h-9" title="Unit price" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-1 gap-2">
                    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                      <input type="checkbox" data-testid={`pkg-item-perguest-${i}`} checked={!!it.per_guest} onChange={(e) => updItem(i, "per_guest", e.target.checked)} /> Scales per guest
                    </label>
                    <span className="text-[11px] text-muted-foreground text-right">
                      {it.per_guest ? `${Number(it.quantity) || 1} × ${bg} guests` : `Qty ${Number(it.quantity) || 1}`} × {inr(Number(it.price) || 0)} = {inr((Number(it.price) || 0) * ((Number(it.quantity) || 1) * (it.per_guest && bg > 0 ? bg : 1)))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 border-t border-stone-200 pt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4" data-testid="pkg-summary">
            <div className="text-sm space-y-1">
              <div className="flex justify-between gap-8 text-muted-foreground"><span>Subtotal</span><span data-testid="pkg-subtotal">{inr(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between gap-8 text-emerald-700"><span>Discount ({discount}%)</span><span>−{inr(subtotal - finalPrice)}</span></div>}
              <div className="flex justify-between gap-8 items-baseline"><span className="font-semibold">Package price</span><span data-testid="pkg-final-price" className="font-display font-extrabold text-2xl text-primary">{inr(finalPrice)}</span></div>
            </div>
            <button data-testid="pkg-save" onClick={save} className="h-11 px-8 bg-primary text-white rounded-full font-semibold shrink-0">{editId ? "Save package" : "Create package"}</button>
          </div>
        </div>
      )}

      <Table
        testid="admin-packages-table"
        columns={[
          { key: "image", label: "", render: (r) => r.image ? <img src={imgUrl(r.image)} alt={r.name} className="h-10 w-14 object-cover rounded" /> : <div className="h-10 w-14 bg-muted rounded" /> },
          { key: "name", label: "Package" },
          { key: "items", label: "Items", render: (r) => (r.items || []).length },
          { key: "subtotal", label: "Subtotal", render: (r) => inr(r.subtotal || 0) },
          { key: "discount_pct", label: "Disc", render: (r) => r.discount_pct ? `${r.discount_pct}%` : "—" },
          { key: "price", label: "Price", render: (r) => inr(r.price) },
          { key: "active", label: "Status", render: (r) => r.active ? <span className="text-emerald-700 font-semibold">Active</span> : <span className="text-muted-foreground">Hidden</span> },
          { key: "featured", label: "Home", render: (r) => r.featured ? <span className="text-primary font-bold" title="Featured on home">★</span> : <span className="text-muted-foreground">—</span> },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`pkg-edit-${r.id}`} onClick={() => openEdit(r)} className="text-blue-600"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`pkg-del-${r.id}`} onClick={() => del(r.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={packages}
      />
    </div>
  );
}

function Bookings() {
  const qc = useQueryClient();
  const { data: bookings = [] } = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: async () => (await api.get("/admin/bookings")).data,
  });
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-bookings"] });
  const cancelBk = async (id) => { await api.put(`/admin/bookings/${id}/cancel`); toast.success("Booking cancelled"); refresh(); };
  const delBk = async (id) => { if (!window.confirm("Remove this booking permanently?")) return; await api.delete(`/admin/bookings/${id}`); toast.success("Booking removed"); refresh(); };
  const [payFilter, setPayFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [active, setActive] = useState(null);

  const PLAN_LABEL = { full: "100% now", half: "50% now", quarter: "25% now", on_event: "On event day" };
  const total = (r) => r.final_total ?? r.subtotal ?? 0;
  const paid = (r) => r.amount_paid || 0;
  const pending = (r) => Math.max(0, total(r) - paid(r));
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  let rows = bookings.filter((r) => {
    const hay = `${r.customer_name || ""} ${r.customer_email || ""} ${r.customer_phone || ""}`.toLowerCase();
    const okQ = hay.includes(q.toLowerCase());
    const okPay = payFilter === "all" || (r.payment_status || "unpaid") === payFilter;
    return okQ && okPay;
  });
  const sorters = {
    newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
    value_high: (a, b) => total(b) - total(a),
    value_low: (a, b) => total(a) - total(b),
    pending_high: (a, b) => pending(b) - pending(a),
  };
  rows = [...rows].sort(sorters[sort]);
  const sum = (f) => rows.reduce((s, r) => s + f(r), 0);

  const exportCSV = () => {
    const headers = ["Customer", "Email", "Phone", "Event", "Event date", "Booked on", "Plan", "Total", "Paid", "Pending", "Payment", "Status"];
    const data = rows.map((r) => [
      r.customer_name, r.customer_email, r.customer_phone, r.event_type, r.event_date, r.created_at,
      PLAN_LABEL[r.payment_plan] || "100% now", total(r), paid(r), pending(r),
      (r.payment_status || "unpaid").replace("_", " "), r.status || "pending",
    ]);
    downloadCSV("bookings", headers, data);
    toast.success("Bookings exported");
  };

  return (
    <div data-testid="admin-bookings">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { l: "Bookings", v: rows.length },
          { l: "Total value", v: inr(sum(total)) },
          { l: "Collected", v: inr(sum(paid)) },
          { l: "Pending", v: inr(sum(pending)) },
        ].map((c) => (
          <div key={c.l} className="bg-card border border-stone-200 rounded-xl p-4">
            <p className="text-xs text-muted-foreground">{c.l}</p>
            <p className="font-display font-extrabold text-lg mt-0.5">{c.v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="bk-search" placeholder="Search name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <select data-testid="bk-payfilter" value={payFilter} onChange={(e) => setPayFilter(e.target.value)} className="h-10 rounded-md border border-input bg-card px-3 text-sm">
          <option value="all">All payments</option>
          <option value="paid">Paid</option>
          <option value="partially_paid">Partially paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <select data-testid="bk-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-md border border-input bg-card px-3 text-sm">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="value_high">Value: High → Low</option>
          <option value="value_low">Value: Low → High</option>
          <option value="pending_high">Pending: High → Low</option>
        </select>
        <button data-testid="bk-export-csv" onClick={exportCSV} className="h-10 px-4 flex items-center gap-2 rounded-md border border-input bg-card text-sm font-semibold hover:bg-accent whitespace-nowrap">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <Table
        testid="admin-bookings-table"
        columns={[
          { key: "customer_name", label: "Customer", render: (r) => (
            <div><p className="font-semibold">{r.customer_name}</p><p className="text-xs text-muted-foreground">{r.customer_email}</p></div>
          )},
          { key: "created_at", label: "Booked on", render: (r) => fmtDate(r.created_at) },
          { key: "event", label: "Event", render: (r) => (
            <div>{r.event_type}<p className="text-xs text-muted-foreground">{fmtDate(r.event_date)}</p></div>
          )},
          { key: "plan", label: "Plan", render: (r) => PLAN_LABEL[r.payment_plan] || "100% now" },
          { key: "total", label: "Total", render: (r) => <span className="font-semibold whitespace-nowrap">{inr(total(r))}</span> },
          { key: "paid", label: "Paid", render: (r) => <span className="text-emerald-700 font-semibold whitespace-nowrap">{inr(paid(r))}</span> },
          { key: "pending", label: "Pending", render: (r) => (
            <span className={`whitespace-nowrap ${pending(r) > 0 ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>{inr(pending(r))}</span>
          )},
          { key: "payment_status", label: "Payment", render: (r) => (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
              r.payment_status === "paid" ? "bg-emerald-100 text-emerald-700"
              : r.payment_status === "partially_paid" ? "bg-blue-100 text-blue-700"
              : "bg-amber-100 text-amber-700"}`}>{(r.payment_status || "unpaid").replace("_", " ")}</span>
          )},
          { key: "status", label: "Status", render: (r) => (
            <span className={`text-xs font-semibold capitalize ${r.status === "cancelled" ? "text-destructive" : "text-muted-foreground"}`}>{r.status || "pending"}</span>
          )},
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`bk-view-${r.id}`} onClick={() => setActive(r)} className="text-primary" title="View"><Eye className="h-4 w-4" /></button>
              <button data-testid={`bk-edit-${r.id}`} onClick={() => { setEditRow(r); setFormOpen(true); }} className="text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`bk-cancel-${r.id}`} onClick={() => cancelBk(r.id)} className="text-amber-600" title="Cancel"><Ban className="h-4 w-4" /></button>
              <button data-testid={`bk-delete-${r.id}`} onClick={() => delBk(r.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={rows}
      />

      <BookingDetail booking={active} onClose={() => setActive(null)} total={total} paid={paid} pending={pending} planLabel={PLAN_LABEL} fmtDate={fmtDate} />
      <BookingForm open={formOpen} row={editRow} onClose={() => setFormOpen(false)} onSaved={refresh} />
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium">{value || "—"}</p></div>
);
const Row = ({ l, v, bold, amber }) => (
  <div className="flex justify-between">
    <span className={amber ? "text-amber-700" : ""}>{l}</span>
    <span className={`${bold ? "font-bold" : ""} ${amber ? "text-amber-700 font-semibold" : ""}`}>{v}</span>
  </div>
);

function BookingDetail({ booking, onClose, total, paid, pending, planLabel, fmtDate }) {
  return (
    <Dialog open={!!booking} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bk-detail">
        {booking && (
          <>
            <DialogHeader><DialogTitle className="font-display">Booking details</DialogTitle></DialogHeader>
            <div className="grid gap-3 text-sm max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                <Info label="Customer" value={booking.customer_name} />
                <Info label="Phone" value={booking.customer_phone} />
                <Info label="Email" value={booking.customer_email} />
                <Info label="Place / City" value={booking.venue ? `${booking.venue}, ${booking.city || ""}` : booking.city} />
                <Info label="Event" value={booking.event_type} />
                <Info label="Event date" value={fmtDate(booking.event_date)} />
                <Info label="Event time" value={booking.event_time} />
                <Info label="Booked on" value={fmtDate(booking.created_at)} />
                <Info label="Plan" value={planLabel[booking.payment_plan] || "100% now"} />
                <Info label="Status" value={booking.status || "pending"} />
              </div>
              <div className="border border-stone-200 rounded-xl overflow-hidden">
                {(booking.items || []).map((it, idx) => (
                  <div key={idx} className="flex justify-between gap-3 px-3 py-2 border-b border-stone-100 last:border-0">
                    <span>{it.name} <span className="text-muted-foreground">× {it.quantity}</span>
                      <br /><span className="text-xs text-muted-foreground">{it.vendor_name}</span></span>
                    <span className="font-semibold whitespace-nowrap">{inr(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-accent/40 rounded-xl p-3 grid gap-1">
                <Row l="Subtotal" v={inr(booking.subtotal || 0)} />
                {booking.discount_pct ? <Row l={`Discount (${booking.discount_pct}%)`} v={"- " + inr((booking.subtotal || 0) - total(booking))} /> : null}
                <Row l="Total" v={inr(total(booking))} bold />
                <Row l="Paid" v={inr(paid(booking))} />
                <Row l="Pending from customer" v={inr(pending(booking))} amber />
              </div>
              {booking.notes ? <p className="text-xs text-muted-foreground">Notes: {booking.notes}</p> : null}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BookingForm({ open, row, onClose, onSaved }) {
  const empty = { customer_name: "", customer_phone: "", customer_email: "", event_type: "", event_date: "", event_time: "", venue: "", city: "Kundapura", final_total: 0, amount_paid: 0, payment_plan: "full", status: "pending", notes: "" };
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (row) setF({ ...empty, ...row, final_total: row.final_total ?? row.subtotal ?? 0, amount_paid: row.amount_paid || 0 });
    else setF(empty);
  }, [row, open]); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.customer_name) return toast.error("Customer name required");
    const payload = { ...f, final_total: Number(f.final_total) || 0, amount_paid: Number(f.amount_paid) || 0 };
    try {
      if (row) await api.put(`/admin/bookings/${row.id}`, payload);
      else await api.post(`/admin/bookings`, payload);
      toast.success(row ? "Booking updated" : "Booking added");
      onClose(); onSaved();
    } catch { toast.error("Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bk-form">
        <DialogHeader><DialogTitle className="font-display">{row ? "Edit booking" : "Add booking"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm max-h-[70vh] overflow-y-auto">
          <Input data-testid="bf-name" placeholder="Customer name" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
          <Input data-testid="bf-phone" placeholder="Phone" value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} />
          <Input placeholder="Email" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
          <Input placeholder="Event type" value={f.event_type} onChange={(e) => set("event_type", e.target.value)} />
          <Input type="date" value={f.event_date || ""} onChange={(e) => set("event_date", e.target.value)} />
          <Input type="time" value={f.event_time || ""} onChange={(e) => set("event_time", e.target.value)} />
          <Input placeholder="Venue / place" value={f.venue} onChange={(e) => set("venue", e.target.value)} />
          <Input placeholder="City" value={f.city} onChange={(e) => set("city", e.target.value)} />
          <Input type="number" placeholder="Total (INR)" value={f.final_total} onChange={(e) => set("final_total", e.target.value)} />
          <Input type="number" placeholder="Paid (INR)" value={f.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} />
          <select value={f.payment_plan} onChange={(e) => set("payment_plan", e.target.value)} className="h-10 rounded-md border border-input bg-card px-3 text-sm">
            <option value="full">100% now</option><option value="half">50% now</option><option value="quarter">25% now</option><option value="on_event">On event day</option>
          </select>
          <select value={f.status} onChange={(e) => set("status", e.target.value)} className="h-10 rounded-md border border-input bg-card px-3 text-sm">
            <option value="pending">Pending</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
          <Textarea className="col-span-2" placeholder="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <button data-testid="bf-save" onClick={save} className="mt-2 h-11 bg-primary text-white rounded-full font-semibold">{row ? "Save changes" : "Add booking"}</button>
      </DialogContent>
    </Dialog>
  );
}

function Enquiries() {
  const { data: enquiries = [] } = useQuery({
    queryKey: ["admin-enquiries"],
    queryFn: async () => (await api.get("/admin/enquiries")).data,
  });
  return (
    <Table
      testid="admin-enquiries-table"
      columns={[
        { key: "customer_name", label: "Customer" },
        { key: "phone", label: "Phone" },
        { key: "email", label: "Email" },
        { key: "event_type", label: "Event" },
        { key: "event_date", label: "Date" },
        { key: "message", label: "Message" },
      ]}
      rows={enquiries}
    />
  );
}

function Applications() {
  const qc = useQueryClient();
  const { data: apps = [] } = useQuery({
    queryKey: ["admin-apps"],
    queryFn: async () => (await api.get("/admin/vendor-applications")).data,
  });
  const act = async (id, action) => {
    await api.put(`/admin/vendor-applications/${id}?action=${action}`);
    toast.success(action === "approve" ? "Approved & vendor created" : "Rejected");
    qc.invalidateQueries({ queryKey: ["admin-apps"] });
    qc.invalidateQueries({ queryKey: ["admin-vendors"] });
  };
  return (
    <Table
      testid="admin-apps-table"
      columns={[
        { key: "business_name", label: "Business" },
        { key: "owner_name", label: "Owner" },
        { key: "city", label: "City" },
        { key: "services_offered", label: "Services" },
        { key: "status", label: "Status" },
        { key: "actions", label: "", render: (r) => r.status === "pending" ? (
          <div className="flex gap-2">
            <button data-testid={`approve-${r.id}`} onClick={() => act(r.id, "approve")} className="px-3 h-8 rounded-full bg-emerald-600 text-white text-xs font-semibold">Approve</button>
            <button data-testid={`reject-${r.id}`} onClick={() => act(r.id, "reject")} className="px-3 h-8 rounded-full border border-stone-300 text-xs font-semibold">Reject</button>
          </div>
        ) : <span className="text-xs text-muted-foreground">{r.status}</span> },
      ]}
      rows={apps}
    />
  );
}

function Finance() {
  const [ok, setOk] = useState(() => sessionStorage.getItem("bme_finance_ok") === "1");
  const [pw, setPw] = useState("");
  const unlock = () => {
    if (pw === "India@123") { sessionStorage.setItem("bme_finance_ok", "1"); setOk(true); }
    else toast.error("Incorrect password");
  };
  if (!ok) {
    return (
      <div className="max-w-sm mx-auto mt-10 bg-card border border-stone-200 rounded-2xl p-6 text-center" data-testid="finance-gate">
        <div className="h-12 w-12 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3"><Lock className="h-6 w-6 text-primary" /></div>
        <h2 className="font-display font-bold text-lg">Finance & Accounts</h2>
        <p className="text-sm text-muted-foreground mb-4">This section is password protected.</p>
        <Input data-testid="finance-password" type="password" placeholder="Enter password" value={pw}
          onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unlock()} className="mb-3" />
        <button data-testid="finance-unlock" onClick={unlock} className="w-full h-11 bg-primary text-white rounded-full font-semibold">Unlock</button>
      </div>
    );
  }
  return <FinancePanel />;
}

function FinancePanel() {
  const qc = useQueryClient();
  const { data: bookings = [] } = useQuery({ queryKey: ["admin-bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });
  const { data: settings } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const { data: expenses = [] } = useQuery({ queryKey: ["admin-expenses"], queryFn: async () => (await api.get("/admin/expenses")).data });
  const { data: payouts = [] } = useQuery({ queryKey: ["admin-payouts"], queryFn: async () => (await api.get("/admin/payouts")).data });
  const [commission, setCommission] = useState("10");
  useEffect(() => { if (settings) setCommission(String(settings.commission_pct)); }, [settings]);

  const c = Number(commission) || 0;
  const rows = bookings.filter((r) => r.status !== "cancelled");
  const total = (r) => r.final_total ?? r.subtotal ?? 0;
  const paid = (r) => r.amount_paid || 0;
  const pendCust = (r) => Math.max(0, total(r) - paid(r));
  const vendorShare = (r) => Math.round(total(r) * (1 - c / 100));
  const commissionAmt = (r) => total(r) - vendorShare(r);
  const sum = (f) => rows.reduce((s, r) => s + f(r), 0);

  const vendorOwed = {};
  rows.forEach((r) => (r.items || []).forEach((it) => {
    if (!it.vendor_id) return;
    vendorOwed[it.vendor_id] = (vendorOwed[it.vendor_id] || 0) + (it.price * it.quantity) * (1 - c / 100);
  }));

  const expPaid = expenses.filter((e) => e.status === "paid").reduce((s, e) => s + (e.amount || 0), 0);
  const expPending = expenses.filter((e) => e.status !== "paid").reduce((s, e) => s + (e.amount || 0), 0);

  const received = sum(paid);
  const totalOwed = sum(vendorShare);
  const totalPaidVendors = payouts.reduce((s, p) => s + (p.amount || 0), 0);
  const pendingToVendors = Math.max(0, totalOwed - totalPaidVendors);
  const netInHand = received - totalPaidVendors - expPaid;

  const saveCommission = async () => {
    await api.put("/admin/settings", { commission_pct: c });
    toast.success("Commission updated");
    qc.invalidateQueries({ queryKey: ["admin-settings"] });
  };

  const exportLedger = () => {
    const headers = ["Customer", "Email", "Total", "Collected", "Pending from customer", "Vendor share", "Commission"];
    const data = rows.map((r) => [r.customer_name, r.customer_email, total(r), paid(r), pendCust(r), vendorShare(r), commissionAmt(r)]);
    downloadCSV("finance-ledger", headers, data);
    toast.success("Ledger exported");
  };

  const cards = [
    { l: "Payment received", v: inr(received), cls: "text-emerald-700" },
    { l: "Pending from customers", v: inr(sum(pendCust)), cls: "text-amber-700" },
    { l: "Owed to vendors", v: inr(totalOwed), cls: "text-stone-700" },
    { l: "Paid to vendors", v: inr(totalPaidVendors), cls: "text-emerald-700" },
    { l: "Pending to vendors", v: inr(pendingToVendors), cls: "text-rose-700" },
    { l: `Commission earned (${c}%)`, v: inr(sum(commissionAmt)), cls: "text-primary" },
    { l: "Expenses paid", v: inr(expPaid), cls: "text-stone-700" },
    { l: "Expenses pending", v: inr(expPending), cls: "text-amber-700" },
    { l: "Net in hand", v: inr(netInHand), cls: netInHand >= 0 ? "text-emerald-700" : "text-rose-700" },
  ];

  return (
    <div data-testid="admin-finance" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c2) => (
          <div key={c2.l} className="bg-card border border-stone-200 rounded-xl p-4">
            <p className="text-xs text-muted-foreground">{c2.l}</p>
            <p className={`font-display font-extrabold text-lg mt-0.5 ${c2.cls}`}>{c2.v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 bg-card border border-stone-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Platform commission</span>
          <Input data-testid="finance-commission" type="number" value={commission} onChange={(e) => setCommission(e.target.value)} className="w-24 h-9" />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
        <button data-testid="finance-save-commission" onClick={saveCommission} className="h-9 px-4 bg-primary text-white rounded-full text-sm font-semibold">Save</button>
        <p className="text-xs text-muted-foreground md:ml-2">Vendor payout = booking total − commission. Rest is your earning.</p>
        <button data-testid="finance-export" onClick={exportLedger} className="md:ml-auto h-9 px-4 flex items-center gap-2 rounded-md border border-input text-sm font-semibold hover:bg-accent"><Download className="h-4 w-4" /> Export ledger</button>
      </div>

      <div>
        <h3 className="font-display font-bold mb-1">Vendor share per booking</h3>
        <p className="text-xs text-muted-foreground mb-3">Amount owed to vendors per booking (total − commission). Record actual payments in <b>Vendor settlements</b> below — that reduces “Pending to vendors” automatically.</p>
        <Table
          testid="finance-payouts-table"
          columns={[
            { key: "customer_name", label: "Booking", render: (r) => (
              <div><p className="font-semibold">{r.customer_name}</p><p className="text-xs text-muted-foreground">{r.event_type}</p></div>
            )},
            { key: "total", label: "Total", render: (r) => <span className="whitespace-nowrap">{inr(total(r))}</span> },
            { key: "collected", label: "Collected", render: (r) => <span className="text-emerald-700 whitespace-nowrap">{inr(paid(r))}</span> },
            { key: "vshare", label: "Vendor share", render: (r) => <span className="whitespace-nowrap">{inr(vendorShare(r))}</span> },
            { key: "commission", label: "Commission", render: (r) => <span className="text-primary whitespace-nowrap">{inr(commissionAmt(r))}</span> },
          ]}
          rows={rows}
        />
      </div>

      <Expenses expenses={expenses} />
      <VendorSettlements owed={totalOwed} vendorOwed={vendorOwed} />
    </div>
  );
}

function Expenses({ expenses }) {
  const qc = useQueryClient();
  const empty = { name: "", category: "Rent", amount: "", status: "pending", expense_date: "", note: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-expenses"] });

  const CATS = ["Rent", "Salary", "Electricity", "Stationery", "Marketing", "Transport", "Other"];

  const save = async () => {
    if (!form.name || !form.amount) return toast.error("Name & amount required");
    const payload = { ...form, amount: Number(form.amount) || 0 };
    try {
      if (editId) await api.put(`/admin/expenses/${editId}`, payload);
      else await api.post("/admin/expenses", payload);
      toast.success(editId ? "Expense updated" : "Expense added");
      setForm(empty); setEditId(null); refresh();
    } catch { toast.error("Failed to save expense"); }
  };
  const edit = (e) => { setEditId(e.id); setForm({ ...empty, ...e, amount: String(e.amount ?? "") }); };
  const toggle = async (e) => { await api.put(`/admin/expenses/${e.id}`, { ...e, status: e.status === "paid" ? "pending" : "paid" }); refresh(); };
  const del = async (id) => { if (!window.confirm("Delete this expense?")) return; await api.delete(`/admin/expenses/${id}`); toast.success("Deleted"); refresh(); };

  return (
    <div data-testid="admin-expenses">
      <h3 className="font-display font-bold mb-3">Operating expenses</h3>
      <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-4 grid md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground">Expense name</label>
          <Input data-testid="exp-name" placeholder="e.g. Office rent - June" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Type</label>
          <select data-testid="exp-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full h-10 rounded-md border border-input bg-card px-3 text-sm">
            {CATS.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Amount (₹)</label>
          <Input data-testid="exp-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Status</label>
          <select data-testid="exp-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full h-10 rounded-md border border-input bg-card px-3 text-sm">
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <button data-testid="exp-save" onClick={save} className="h-10 bg-primary text-white rounded-full font-semibold">{editId ? "Save" : "Add"}</button>
        <div className="md:col-span-4">
          <Input placeholder="Note (optional)" value={form.note} onChange={(e) => set("note", e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <Input type="date" value={form.expense_date || ""} onChange={(e) => set("expense_date", e.target.value)} />
        </div>
      </div>

      <Table
        testid="admin-expenses-table"
        columns={[
          { key: "name", label: "Expense", render: (r) => (
            <div><p className="font-semibold">{r.name}</p>{r.note ? <p className="text-xs text-muted-foreground">{r.note}</p> : null}</div>
          )},
          { key: "category", label: "Type" },
          { key: "expense_date", label: "Date", render: (r) => r.expense_date || "—" },
          { key: "amount", label: "Amount", render: (r) => <span className="font-semibold whitespace-nowrap">{inr(r.amount)}</span> },
          { key: "status", label: "Status", render: (r) => (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${r.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
          )},
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`exp-toggle-${r.id}`} onClick={() => toggle(r)} className="text-xs font-semibold text-emerald-700">{r.status === "paid" ? "Mark pending" : "Mark paid"}</button>
              <button data-testid={`exp-edit-${r.id}`} onClick={() => edit(r)} className="text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`exp-del-${r.id}`} onClick={() => del(r.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={expenses}
      />
    </div>
  );
}

function Catalog() {
  const qc = useQueryClient();
  const empty = { name: "", type: "", unit: "per event" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { data: items = [] } = useQuery({ queryKey: ["admin-catalog"], queryFn: async () => (await api.get("/admin/catalog")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-catalog"] });
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) await api.put(`/admin/catalog/${editId}`, form);
      else await api.post("/admin/catalog", form);
      toast.success(editId ? "Item updated" : "Item added");
      setForm(empty); setEditId(null); refresh();
    } catch { toast.error("Failed to save item"); }
  };
  const edit = (r) => { setEditId(r.id); setForm({ ...empty, ...r }); };
  const del = async (id) => { if (!window.confirm("Delete this item from the master list?")) return; await api.delete(`/admin/catalog/${id}`); toast.success("Deleted"); refresh(); };
  return (
    <div data-testid="admin-catalog">
      <p className="text-sm text-muted-foreground mb-4">Master list of all services & items — no price here. Set prices per vendor from the <b>Vendors</b> tab (edit a vendor → Services offered & prices).</p>
      <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-4 grid md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground">Service / item name</label>
          <Input data-testid="cat-name" placeholder="e.g. Stage Decoration" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Type / category</label>
          <Input data-testid="cat-type" placeholder="e.g. Decor, DJ, Catering" value={form.type} onChange={(e) => set("type", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Unit</label>
          <Input placeholder="per event" value={form.unit} onChange={(e) => set("unit", e.target.value)} />
        </div>
        <button data-testid="cat-save" onClick={save} className="md:col-span-4 md:w-40 h-10 bg-primary text-white rounded-full font-semibold">{editId ? "Save item" : "Add item"}</button>
      </div>
      <Table
        testid="admin-catalog-table"
        columns={[
          { key: "name", label: "Service / item" },
          { key: "type", label: "Type", render: (r) => r.type || "—" },
          { key: "unit", label: "Unit" },
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button data-testid={`cat-edit-${r.id}`} onClick={() => edit(r)} className="text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`cat-del-${r.id}`} onClick={() => del(r.id)} className="text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          )},
        ]}
        rows={items}
      />
    </div>
  );
}

function VendorServices({ vendorId }) {
  const qc = useQueryClient();
  const { data: catalog = [] } = useQuery({ queryKey: ["admin-catalog"], queryFn: async () => (await api.get("/admin/catalog")).data });
  const { data: services = [] } = useQuery({ queryKey: ["admin-services"], queryFn: async () => (await api.get("/services")).data });
  const mine = services.filter((s) => s.vendor_id === vendorId);
  const findSvc = (item) => mine.find((s) => s.name === item.name);
  const [prices, setPrices] = useState({});
  const seededRef = useRef(null);
  useEffect(() => {
    if (seededRef.current === vendorId) return;
    if (!catalog.length) return;
    const init = {};
    catalog.forEach((item) => { const s = mine.find((x) => x.name === item.name); if (s) init[item.id] = String(s.price ?? ""); });
    setPrices(init);
    seededRef.current = vendorId;
  }, [catalog, services, vendorId]); // eslint-disable-line
  const setPrice = (id, v) => setPrices((p) => ({ ...p, [id]: v }));
  const saveRow = async (item) => {
    const price = Number(prices[item.id] || 0);
    const existing = findSvc(item);
    try {
      if (existing) await api.put(`/admin/services/${existing.id}`, { vendor_id: vendorId, name: item.name, type: item.type || existing.type || "", price, unit: item.unit || existing.unit || "per event", image: existing.image || "", desc: existing.desc || "" });
      else await api.post("/admin/services", { vendor_id: vendorId, name: item.name, type: item.type || "", price, unit: item.unit || "per event", image: "", desc: "" });
      toast.success(`Saved ${item.name}`);
      qc.invalidateQueries({ queryKey: ["admin-services"] });
    } catch { toast.error("Failed to save price"); }
  };
  const removeRow = async (item) => {
    const existing = findSvc(item);
    if (!existing) return;
    await api.delete(`/admin/services/${existing.id}`);
    setPrice(item.id, "");
    toast.success("Removed");
    qc.invalidateQueries({ queryKey: ["admin-services"] });
  };
  if (!catalog.length) return <p className="text-sm text-muted-foreground">No items in the master list yet. Add them under the <b>Service List</b> tab first.</p>;
  return (
    <div className="border border-stone-200 rounded-xl overflow-x-auto" data-testid="vendor-services">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-stone-200 text-left text-muted-foreground"><th className="px-3 py-2">Service / item</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 w-40">Price (₹)</th><th className="px-3 py-2"></th></tr></thead>
        <tbody>
          {catalog.map((item) => {
            const active = !!findSvc(item);
            return (
              <tr key={item.id} className="border-b border-stone-100 last:border-0">
                <td className="px-3 py-2 font-medium">{item.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{item.type || "—"}</td>
                <td className="px-3 py-2"><Input data-testid={`vs-price-${item.id}`} type="number" value={prices[item.id] ?? ""} onChange={(e) => setPrice(item.id, e.target.value)} className="h-9" /></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button type="button" data-testid={`vs-save-${item.id}`} onClick={() => saveRow(item)} className="px-3 h-8 rounded-full bg-primary text-white text-xs font-semibold">{active ? "Update" : "Add"}</button>
                  {active && <button type="button" data-testid={`vs-remove-${item.id}`} onClick={() => removeRow(item)} className="ml-2 text-destructive text-xs font-semibold">Remove</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Customers() {
  const { data: bookings = [] } = useQuery({ queryKey: ["admin-bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });
  const [q, setQ] = useState("");
  const total = (r) => r.final_total ?? r.subtotal ?? 0;
  const map = {};
  bookings.filter((b) => b.status !== "cancelled").forEach((b) => {
    const key = (b.customer_email || b.customer_phone || b.customer_name || "unknown").toLowerCase();
    if (!map[key]) map[key] = { name: "", email: "", phone: "", city: "", count: 0, spend: 0, collected: 0, last: "" };
    const c = map[key];
    c.count += 1; c.spend += total(b); c.collected += b.amount_paid || 0;
    c.name = c.name || b.customer_name; c.email = c.email || b.customer_email; c.phone = c.phone || b.customer_phone; c.city = c.city || b.city;
    const when = b.event_date || b.created_at || "";
    if (when > c.last) c.last = when;
  });
  let rows = Object.values(map).filter((c) => `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()));
  rows = rows.sort((a, b) => b.spend - a.spend);
  const exportCSV = () => {
    downloadCSV("customers", ["Name", "Email", "Phone", "City", "Bookings", "Total value", "Collected", "Last event"],
      rows.map((c) => [c.name, c.email, c.phone, c.city, c.count, c.spend, c.collected, c.last]));
    toast.success("Customers exported");
  };
  return (
    <div data-testid="admin-customers">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { l: "Customers", v: rows.length },
          { l: "Repeat customers", v: rows.filter((c) => c.count > 1).length },
          { l: "Total booking value", v: inr(rows.reduce((s, c) => s + c.spend, 0)) },
          { l: "Collected", v: inr(rows.reduce((s, c) => s + c.collected, 0)) },
        ].map((c) => (
          <div key={c.l} className="bg-card border border-stone-200 rounded-xl p-4">
            <p className="text-xs text-muted-foreground">{c.l}</p>
            <p className="font-display font-extrabold text-lg mt-0.5">{c.v}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="cust-search" placeholder="Search name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <button data-testid="cust-export" onClick={exportCSV} className="h-10 px-4 flex items-center gap-2 rounded-md border border-input bg-card text-sm font-semibold hover:bg-accent whitespace-nowrap"><Download className="h-4 w-4" /> Export CSV</button>
      </div>
      <Table
        testid="admin-customers-table"
        columns={[
          { key: "name", label: "Customer", render: (r) => (
            <div className="flex items-center gap-2">
              <div><p className="font-semibold">{r.name || "—"}</p><p className="text-xs text-muted-foreground">{r.email}</p></div>
              {r.count > 1 ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">Repeat</span> : null}
            </div>
          )},
          { key: "phone", label: "Phone" },
          { key: "city", label: "City" },
          { key: "count", label: "Bookings", render: (r) => <span className="font-semibold">{r.count}</span> },
          { key: "spend", label: "Total value", render: (r) => <span className="font-semibold whitespace-nowrap">{inr(r.spend)}</span> },
          { key: "collected", label: "Collected", render: (r) => <span className="text-emerald-700 whitespace-nowrap">{inr(r.collected)}</span> },
          { key: "last", label: "Last event", render: (r) => fmtDay(r.last) },
        ]}
        rows={rows}
      />
    </div>
  );
}

function InquiriesInbox() {
  const qc = useQueryClient();
  const { data: msgs = [] } = useQuery({ queryKey: ["admin-inbox"], queryFn: async () => (await api.get("/admin/inbox")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["admin-vendors"], queryFn: async () => (await api.get("/admin/vendors")).data });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");
  const vname = (id) => vendors.find((v) => v.id === id)?.name || "";
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-inbox"] });
  const rows = msgs.filter((m) => filter === "all" || (m.status || "new") === filter);
  const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
  const counts = { all: msgs.length, new: msgs.filter((m) => (m.status || "new") === "new").length, replied: msgs.filter((m) => m.status === "replied").length };
  const send = async () => {
    if (!reply.trim()) return toast.error("Write a reply first");
    try {
      await api.post("/admin/inbox/reply", { source: active.source, id: active.id, message: reply });
      toast.success("Reply sent & emailed");
      setReply(""); setActive(null); refresh();
    } catch { toast.error("Failed to send reply"); }
  };
  const del = async (m) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await api.delete(`/admin/inbox/${m.source}/${m.id}`);
      toast.success("Message deleted");
      if (active && active.id === m.id) setActive(null);
      refresh();
    } catch { toast.error("Failed to delete"); }
  };
  return (
    <div data-testid="admin-inbox">
      <div className="flex gap-2 mb-4">
        {["all", "new", "replied"].map((f) => (
          <button key={f} data-testid={`inbox-filter-${f}`} onClick={() => setFilter(f)}
            className={`h-9 px-4 rounded-full text-sm font-semibold capitalize ${filter === f ? "bg-primary text-white" : "border border-stone-300 hover:bg-accent"}`}>
            {f} ({counts[f]})
          </button>
        ))}
      </div>
      <div className="grid gap-3">
        {rows.length === 0 && <p className="text-muted-foreground" data-testid="admin-inbox-empty">No messages yet.</p>}
        {rows.map((m) => (
          <div key={`${m.source}-${m.id}`} className="bg-card border border-stone-200 rounded-xl p-4 hover:border-primary transition-colors">
            <div className="flex items-start justify-between gap-2">
              <button data-testid={`inbox-item-${m.id}`} onClick={() => { setActive(m); setReply(""); }} className="text-left flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{m.name || "—"}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent">{m.source === "enquiry" ? (vname(m.vendor_id) ? `Vendor · ${vname(m.vendor_id)}` : "Vendor enquiry") : "Website"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${m.status === "replied" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{m.status || "new"}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{m.message || (m.event_type ? `Interested in ${m.event_type}` : "—")}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.email || "no email"} · {m.phone || "no phone"} · {fmt(m.created_at)}</p>
              </button>
              <button data-testid={`inbox-del-${m.id}`} onClick={() => del(m)} className="text-destructive shrink-0" title="Delete message"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-lg" data-testid="inbox-detail">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-display">Message from {active.name || "customer"}</DialogTitle></DialogHeader>
              <div className="grid gap-3 text-sm max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  <Info label="Email" value={active.email} />
                  <Info label="Phone" value={active.phone} />
                  {active.event_type ? <Info label="Event" value={active.event_type} /> : null}
                  {active.event_date ? <Info label="Event date" value={active.event_date} /> : null}
                  {active.source === "enquiry" && vname(active.vendor_id) ? <Info label="Vendor" value={vname(active.vendor_id)} /> : null}
                </div>
                <div className="bg-accent/40 rounded-xl p-3"><p className="whitespace-pre-wrap">{active.message || "—"}</p></div>
                {(active.replies || []).map((r, i) => (
                  <div key={i} className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                    <p className="text-xs text-emerald-700 font-semibold mb-1">Your reply · {fmt(r.at)}</p>
                    <p className="whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
                <Textarea data-testid="inbox-reply-text" rows={4} placeholder="Type your reply (emailed to the customer)…" value={reply} onChange={(e) => setReply(e.target.value)} />
                <button data-testid="inbox-reply-send" onClick={send} className="h-11 bg-primary text-white rounded-full font-semibold">Send reply</button>
                <button data-testid="inbox-delete" onClick={() => del(active)} className="h-10 border border-destructive text-destructive rounded-full font-semibold">Delete message</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Reminders() {
  const qc = useQueryClient();
  const { data: cands = [], isLoading } = useQuery({ queryKey: ["admin-reminders"], queryFn: async () => (await api.get("/admin/reorder-reminders/preview")).data });
  const [sending, setSending] = useState(false);
  const due = cands.filter((c) => !c.already_sent);
  const fmt = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const run = async () => {
    if (!due.length) return toast("No reminders due right now");
    if (!window.confirm(`Send ${due.length} reminder email(s) now?`)) return;
    setSending(true);
    try { const { data } = await api.post("/admin/reorder-reminders/run"); toast.success(`Sent ${data.sent} reminder(s)`); qc.invalidateQueries({ queryKey: ["admin-reminders"] }); }
    catch { toast.error("Failed to send reminders"); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="admin-reminders">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-display font-bold text-xl">Anniversary reorder reminders</h2>
          <p className="text-sm text-muted-foreground">Customers whose event anniversary falls in the next 14 days. Emails also go out automatically every morning.</p>
        </div>
        <button data-testid="reminders-send" onClick={run} disabled={sending || due.length === 0} className="h-10 px-5 bg-primary text-white rounded-full text-sm font-semibold disabled:opacity-50">
          {sending ? "Sending…" : `Send now (${due.length})`}
        </button>
      </div>
      {cands.length === 0 && !isLoading ? (
        <p className="text-muted-foreground py-10 text-center" data-testid="reminders-empty">No anniversaries coming up in the next 14 days.</p>
      ) : (
        <Table
          testid="admin-reminders-table"
          columns={[
            { key: "name", label: "Customer", render: (r) => (<div><p className="font-semibold">{r.name || "—"}</p><p className="text-xs text-muted-foreground">{r.email}</p></div>) },
            { key: "event_type", label: "Event", render: (r) => r.event_type || "—" },
            { key: "event_date", label: "Original date", render: (r) => fmt(r.event_date) },
            { key: "anniversary", label: "Anniversary", render: (r) => fmt(r.anniversary) },
            { key: "vendors", label: "Vendors", render: (r) => r.vendors || "—" },
            { key: "already_sent", label: "Status", render: (r) => r.already_sent
              ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Sent</span>
              : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Due</span> },
          ]}
          rows={cands}
        />
      )}
    </div>
  );
}

function VendorSettlements({ owed = 0, vendorOwed = {} }) {
  const qc = useQueryClient();
  const { data: vendors = [] } = useQuery({ queryKey: ["admin-vendors"], queryFn: async () => (await api.get("/admin/vendors")).data });
  const { data: payouts = [] } = useQuery({ queryKey: ["admin-payouts"], queryFn: async () => (await api.get("/admin/payouts")).data });
  const empty = { vendor_id: "", amount: "", paid_on: new Date().toISOString().slice(0, 10), note: "" };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-payouts"] });
  const save = async () => {
    if (!form.vendor_id || !form.amount) return toast.error("Vendor & amount required");
    try {
      await api.post("/admin/payouts", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Payout recorded");
      setForm(empty); refresh();
    } catch { toast.error("Failed to record payout"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this payout record?")) return; await api.delete(`/admin/payouts/${id}`); toast.success("Deleted"); refresh(); };
  const downloadStatement = async (vendorId, vendorName) => {
    try {
      const res = await api.get(`/admin/vendors/${vendorId}/statement`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statement-${(vendorName || "vendor").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate statement"); }
  };
  const totalPaid = payouts.reduce((s, p) => s + (p.amount || 0), 0);
  const pending = Math.max(0, owed - totalPaid);
  const fmt = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const paidByVendor = {};
  payouts.forEach((p) => { paidByVendor[p.vendor_id] = (paidByVendor[p.vendor_id] || 0) + (p.amount || 0); });
  const perVendor = Array.from(new Set([...Object.keys(vendorOwed), ...Object.keys(paidByVendor)]))
    .map((id) => { const v = vendors.find((x) => x.id === id); return v ? { id, name: v.name, owed: Math.round(vendorOwed[id] || 0), paid: paidByVendor[id] || 0 } : null; })
    .filter(Boolean)
    .map((r) => ({ ...r, pending: Math.max(0, r.owed - r.paid) }))
    .filter((r) => r.owed > 0 || r.paid > 0)
    .sort((a, b) => b.pending - a.pending);
  return (
    <div data-testid="admin-settlements">
      <h3 className="font-display font-bold mb-1">Vendor settlements</h3>
      <p className="text-xs text-muted-foreground mb-3">Record every payout you make to a vendor, with the date. Owed to vendors: <b>{inr(owed)}</b> · Paid: <b className="text-emerald-700">{inr(totalPaid)}</b> · <span data-testid="settlements-pending">Pending: <b className="text-rose-700">{inr(pending)}</b></span>. Recording a payout here reduces “Pending to vendors” automatically.</p>

      {perVendor.length > 0 && (
        <div className="mb-6" data-testid="vendor-balances">
          <p className="text-sm font-semibold mb-2">Balance owed per vendor <span className="text-xs text-muted-foreground font-normal">(from itemised bookings)</span></p>
          <Table
            testid="admin-vendor-balances-table"
            columns={[
              { key: "name", label: "Vendor", render: (r) => <span className="font-semibold">{r.name}</span> },
              { key: "owed", label: "Owed", render: (r) => <span className="whitespace-nowrap">{inr(r.owed)}</span> },
              { key: "paid", label: "Paid", render: (r) => <span className="text-emerald-700 whitespace-nowrap">{inr(r.paid)}</span> },
              { key: "pending", label: "Still owed", render: (r) => <span className={`whitespace-nowrap font-semibold ${r.pending > 0 ? "text-rose-700" : "text-muted-foreground"}`}>{inr(r.pending)}</span> },
              { key: "actions", label: "", render: (r) => (
                <div className="flex gap-2 whitespace-nowrap">
                  <button data-testid={`pay-vendor-quick-${r.id}`} disabled={r.pending <= 0} onClick={() => setForm({ ...form, vendor_id: r.id, amount: String(r.pending) })} className="px-3 h-8 rounded-full border border-stone-300 text-xs font-semibold disabled:opacity-50">Pay balance</button>
                  <button data-testid={`vendor-statement-${r.id}`} onClick={() => downloadStatement(r.id, r.name)} className="px-3 h-8 rounded-full border border-stone-300 text-xs font-semibold flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Statement</button>
                </div>
              )},
            ]}
            rows={perVendor}
          />
        </div>
      )}

      <div className="bg-card border border-stone-200 rounded-2xl p-5 mb-4 grid md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground">Vendor</label>
          <select data-testid="pay-vendor" value={form.vendor_id} onChange={(e) => set("vendor_id", e.target.value)} className="w-full h-10 rounded-md border border-input bg-card px-3 text-sm">
            <option value="">Select vendor</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-muted-foreground">Amount (₹)</label><Input data-testid="pay-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
        <div><label className="text-xs text-muted-foreground">Paid on</label><Input data-testid="pay-date" type="date" value={form.paid_on} onChange={(e) => set("paid_on", e.target.value)} /></div>
        <button data-testid="pay-save" onClick={save} className="h-10 bg-primary text-white rounded-full font-semibold">Record</button>
        <div className="md:col-span-5"><Input placeholder="Note (optional, e.g. UPI/bank reference)" value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
      </div>
      <Table
        testid="admin-settlements-table"
        columns={[
          { key: "vendor_name", label: "Vendor", render: (r) => r.vendor_name || "—" },
          { key: "amount", label: "Amount", render: (r) => <span className="font-semibold whitespace-nowrap">{inr(r.amount)}</span> },
          { key: "paid_on", label: "Paid on", render: (r) => fmt(r.paid_on) },
          { key: "note", label: "Note", render: (r) => r.note || "—" },
          { key: "actions", label: "", render: (r) => <button data-testid={`pay-del-${r.id}`} onClick={() => del(r.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button> },
        ]}
        rows={payouts}
      />
    </div>
  );
}
